export interface AuthTokenData {
  isAuthenticated: boolean;
  expiresAt: string;
  lastUpdated: string;
  sessionId?: string; // Добавляем уникальный идентификатор сессии
}

const STORAGE_KEY = 'horoscope_auth_state';
const SESSION_KEY = 'horoscope_session_id';
const DEFAULT_TOKEN_LIFETIME_MINUTES = 60;

export class TokenStorage {
  static saveAuthState(isAuth: boolean, expiresInMinutes: number = DEFAULT_TOKEN_LIFETIME_MINUTES): void {
    try {
      const expiresAt = new Date(Date.now() + expiresInMinutes * 60 * 1000);
      const sessionId = this.getOrCreateSessionId();
      
      const authData: AuthTokenData = {
        isAuthenticated: isAuth,
        expiresAt: expiresAt.toISOString(),
        lastUpdated: new Date().toISOString(),
        sessionId: sessionId
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
    } catch (error) {
      console.log("Ошибка при сохранении токена в localStorage:", error);
    }
  }

  static getAuthState(): AuthTokenData | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as AuthTokenData;
      }
    } catch (error) {
      console.log("Ошибка при получении токена из localStorage:", error);
    }
    return null;
  }

  static isTokenValid(): boolean {
    try {
      const authData = this.getAuthState();
      if (authData && authData.isAuthenticated && authData.expiresAt) {
        // Проверяем, что сессия не была сброшена
        if (authData.sessionId && authData.sessionId !== this.getCurrentSessionId()) {
          console.log("Сессия была сброшена, токен недействителен");
          return false;
        }
        return new Date(authData.expiresAt) > new Date();
      }
    } catch (error) {
      console.log("Ошибка при проверке валидности токена:", error);
    }
    return false;
  }

  static clearAuthState(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      // Сбрасываем сессию при разлогинивании
      this.resetSession();
    } catch (error) {
      console.log("Ошибка при очистке токена из localStorage:", error);
    }
  }

  static getTokenAge(): number | null {
    try {
      const authData = this.getAuthState();
      if (authData && authData.lastUpdated) {
        const lastUpdated = new Date(authData.lastUpdated);
        return Date.now() - lastUpdated.getTime();
      }
    } catch (error) {
      console.log("Ошибка при получении возраста токена:", error);
    }
    return null;
  }

  static shouldRefreshToken(): boolean {
    try {
      const authData = this.getAuthState();
      if (authData && authData.expiresAt) {
        const expiresAt = new Date(authData.expiresAt);
        const now = new Date();
        const timeUntilExpiry = expiresAt.getTime() - now.getTime();
        // Обновляем токен за 5 минут до истечения
        return timeUntilExpiry < 5 * 60 * 1000;
      }
    } catch (error) {
      console.log("Ошибка при проверке необходимости обновления токена:", error);
    }
    return false;
  }

  // Методы для управления сессией
  private static getOrCreateSessionId(): string {
    let sessionId = this.getCurrentSessionId();
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  private static getCurrentSessionId(): string | null {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch (error) {
      console.log("Ошибка при получении ID сессии:", error);
      return null;
    }
  }

  private static resetSession(): void {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.log("Ошибка при сбросе сессии:", error);
    }
  }

  // Метод для принудительного сброса сессии (например, при разлогинивании в другом окне)
  static forceLogout(): void {
    this.resetSession();
    this.clearAuthState();
  }
} 